<script lang="ts">
import { defineComponent, PropType } from 'vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import InputForm from '@/components/form/InputForm.vue'
import InputFormKana from '@/components/form/InputFormKana.vue'
import InputFormKanji from '@/components/lead/InputFormKanji.vue'
import PaginationRegistrationStep from '@/components/user/registration/PaginationRegistrationStep.vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import validationOperator from '@/models/validationOperator'
import { newUserSessionPath } from '@/generated/railsRoutes'

export default defineComponent({
  name: 'InputStep1UserRegistrationForShowContents',

  components: {
    ButtonSubmit,
    InputForm,
    InputFormKana,
    InputFormKanji,
    PaginationRegistrationStep,
    ValidationObserver,
  },

  props: {
    namespace: {
      type: String,
      default: '',
    },

    lastName: {
      type: Object as PropType<InputText>,
      required: true,
    },

    firstName: {
      type: Object as PropType<InputText>,
      required: true,
    },

    lastNameKana: {
      type: Object as PropType<InputText>,
      required: true,
    },

    firstNameKana: {
      type: Object as PropType<InputText>,
      required: true,
    },

    workMailAddress: {
      type: Object as PropType<InputText>,
      required: true,
    },
  },

  emits: [
    'update:lastName',
    'update:firstName',
    'update:lastNameKana',
    'update:firstNameKana',
    'update:workMailAddress',
    'click:next',
  ],

  setup(props, { emit }) {
    const { validationObserver, validate } = validationOperator()
    const next = () => {
      emit('click:next')
    }

    return {
      // routes
      newUserSessionPath,

      // data
      validationObserver,

      // computed

      // methods
      validate,
      next,
    }
  }
})
</script>

<template>
  <ValidationObserver
    ref="validationObserver"
    :namespace="namespace"
    @valid="next">
    <div class="l-rows">
      <!-- 会員登録導線 -->
      <div class="l-rows--row">
        <div class="o-heading__section">
          <h2
            class="o-heading__section--item u-text__before u-text__after"
            data-before="＼"
            data-after="／">
            サクッと1分で登録！
          </h2>
        <!-- /.o-heading__section --></div>

        <p class="p u-text__center">
          <slot name="headingDescription">
            こちらは<strong class="strong">会員様限定</strong>の貴重な資料です。<br>無料会員登録で資料をダウンロードいただけます。
          </slot>
        </p>

        <PaginationRegistrationStep :current-step="1" />

        <div class="l-rows">
          <!-- 氏名 -->
          <div class="l-rows--row">
            <div class="l-cols is-col2">
              <!-- 姓 -->
              <div
                v-if="lastName"
                class="l-cols--col">
                <InputFormKanji
                  :model-value="lastName.value"
                  :model-value-kana="lastNameKana.value"
                  :name="lastName.name"
                  :namespace="namespace"
                  header="姓"
                  placeholder="例：田中"
                  :validation-scheme="lastName.validationScheme"
                  @update:model-value="$emit('update:lastName', $event)"
                  @update:model-value-kana="$emit('update:lastNameKana', $event)" />
              <!-- /.l-cols col --></div>

              <!-- 名 -->
              <div
                v-if="firstName"
                class="l-cols--col">
                <InputFormKanji
                  :model-value="firstName.value"
                  :model-value-kana="firstNameKana.value"
                  :name="firstName.name"
                  :namespace="namespace"
                  header="名"
                  placeholder="例：太郎"
                  :validation-scheme="firstName.validationScheme"
                  @update:model-value="$emit('update:firstName', $event)"
                  @update:model-value-kana="$emit('update:firstNameKana', $event)" />
              <!-- /.l-cols col --></div>
            <!-- /.l-cols --></div>
          <!-- /.l-rows row --></div>

          <!-- 氏名（かな） -->
          <div class="l-rows--row">
            <div class="l-cols is-col2">
              <!-- 姓（かな） -->
              <div
                v-if="lastNameKana"
                class="l-cols--col">
                <InputFormKana
                  :model-value="lastNameKana.value"
                  :namespace="namespace"
                  :name="lastNameKana.name"
                  header="姓（かな）"
                  placeholder="例：たなか"
                  :validation-scheme="lastNameKana.validationScheme"
                  @update:model-value="$emit('update:lastNameKana', $event)" />
              <!-- /.l-cols col --></div>

              <!-- 名（かな） -->
              <div
                v-if="firstNameKana"
                class="l-cols--col">
                <InputFormKana
                  :model-value="firstNameKana.value"
                  :namespace="namespace"
                  :name="firstNameKana.name"
                  header="名（かな）"
                  placeholder="例：たろう"
                  :validation-scheme="firstNameKana.validationScheme"
                  @update:model-value="$emit('update:firstNameKana', $event)" />
              <!-- /.l-cols col --></div>
            <!-- /.l-cols --></div>
          <!-- /.l-rows row --></div>

          <!-- 勤務先メールアドレス -->
          <div
            v-if="workMailAddress"
            class="l-rows--row">
            <InputForm
              :model-value="workMailAddress.value"
              :namespace="namespace"
              type="email"
              :name="workMailAddress.name"
              header="勤務先メールアドレス"
              placeholder="例：bizhint@bizhint.jp"
              :validation-scheme="workMailAddress.validationScheme"
              @update:model-value="$emit('update:workMailAddress', $event)" />
          <!-- /.l-rows row --></div>
        <!-- /.l-rows --></div>

        <ButtonSubmit
          :is-link="false"
          :has-shadow="false"
          prevent
          stop
          @click:submit="validate">
          <slot name="submitLabel">
            登録して資料を受け取る(無料)
          </slot>
        </ButtonSubmit>
      <!-- /.l-rows row --></div>

      <!-- ログイン導線 -->
      <div class="l-rows--row">
        <p class="p u-text__center u-text__small">
          すでに会員の方は
          <a
            class="a"
            :href="newUserSessionPath()">こちら</a>
          からログイン
        </p>
      <!-- /.l-rows row --></div>
    <!-- /.l-rows --></div>
  </ValidationObserver>
</template>

<style lang="sass" scoped>
// object
.o-heading__section
  & &--item
    font:
      size: $font-normal
      weight: bold
    text-align: center

  + .p
    margin-top: 0.5em
</style>
